/**
 * The AI tab's read path — resolution #2 of task-16-brief.md: pane C's AI
 * tab must read through the operations layer, not the store, even though
 * Phase 1 has no AI operation defined yet (packages/shared/src/operations.ts
 * has no `ai-*` entry in OperationName, and services/ops has no /ops/ai
 * route). Rather than hardcode the seam copy straight into a component, this
 * makes the same shape of call the console's other reads make
 * (../../lib/ops.ts's `createOpsClient` factory: injectable fetch + base
 * URL, resolved from runtime-config when not overridden) and falls back to
 * the seam state on any failure — a 404 today, a real "not wired yet" JSON
 * answer if one ever ships, or a network error. When a later phase adds a
 * genuine endpoint here, this is the one function that starts returning
 * real content; no component changes.
 *
 * Deliberately NOT added to ../../lib/ops.ts itself: this task's scope is
 * apps/console/src/components/context/** plus its own test file, and other
 * agents are concurrently building services/ops and the thread pane — this
 * module only reads, never calls `ops.call`, so it cannot collide with the
 * operation set they own.
 */
import { loadRuntimeConfig } from '../../runtime-config'

export interface AiSuggestion {
  id: string
  text: string
}

/** 1 Critical .. 5 Next Time — the Halo names, so the chip reads like a ticket. */
export type PriorityLevel = 1 | 2 | 3 | 4 | 5

export interface AiPaneRead {
  /** True only once a real endpoint answers with wired content. */
  wired: boolean
  suggestions: AiSuggestion[]
  /** Absent until something has actually been read. */
  priority?: PriorityLevel
  /**
   * Why there is nothing to show, when the route answered but could not help.
   *
   * The seam collapsed "not built" and "not working" into one grey card, which
   * was fine while nothing was built. Now that it is, a dispatcher needs to
   * tell "an admin switched this off" from "the key expired" from "rate
   * limited, try in a minute" — three different responses.
   */
  unavailable?: string
  /** A read is in flight. Distinct from the seam, and from a failure. */
  loading?: boolean
  /**
   * No conversation is open yet, so there is nothing to have hints ABOUT.
   *
   * The third thing the seam card was standing in for. On a fresh load the
   * pane showed "NOT WIRED · SEAM ONLY" before anyone had clicked anything,
   * which reads as a broken feature rather than an empty one.
   */
  idle?: boolean
}

/** Three dashed placeholders, not per-thread fabricated content — resolution #1/#5: nothing here
 * pretends to have read this specific thread, because nothing has. */
const SEAM_SUGGESTIONS: readonly AiSuggestion[] = [
  { id: 'seam-1', text: 'Next question — appears once the AI operator is wired.' },
  { id: 'seam-2', text: 'Tone read — appears once the AI operator is wired.' },
  { id: 'seam-3', text: 'Suggested action — appears once the AI operator is wired.' },
]

export const AI_SEAM_STATE: AiPaneRead = {
  wired: false,
  suggestions: [...SEAM_SUGGESTIONS],
}

/**
 * What the pane shows while a read is in flight.
 *
 * NOT the seam. The seam says "appears once the AI operator is wired", which
 * was true in Phase 1 and is now a lie — it IS wired, it is thinking. Showing
 * the unbuilt-state copy for the three seconds a first read takes told
 * everyone the feature did not exist.
 */
export const AI_LOADING_STATE: AiPaneRead = {
  wired: false,
  suggestions: [],
  loading: true,
}

/** Nothing open yet — see `idle`. */
export const AI_NO_THREAD_STATE: AiPaneRead = {
  wired: false,
  suggestions: [],
  idle: true,
}

interface AiOpsDeps {
  /** Defaults to the global `fetch`. Override in tests. */
  fetchImpl?: typeof fetch
  /** Defaults to reading /runtime-config.json via loadRuntimeConfig(). Pass a literal (or a function
   * returning one) in tests to skip that fetch entirely — mirrors ../../lib/ops.ts's own deps shape. */
  baseUrl?: string | (() => string | Promise<string>)
}

function isAiPaneReadShape(body: unknown): body is Partial<AiPaneRead> {
  return typeof body === 'object' && body !== null
}

/**
 * Builds the AI-pane read client. `read()` always resolves — it never
 * throws — because a gate refusal or a missing route both mean the same
 * thing to this tab: show the seam, not an error.
 */
export function createAiOpsClient(deps: AiOpsDeps = {}) {
  const doFetch: typeof fetch = deps.fetchImpl ?? ((...args) => fetch(...args))

  async function resolveBase(): Promise<string> {
    if (deps.baseUrl !== undefined) {
      return typeof deps.baseUrl === 'function' ? deps.baseUrl() : deps.baseUrl
    }
    return (await loadRuntimeConfig()).apiBase
  }

  async function read(threadId: string): Promise<AiPaneRead> {
    try {
      const base = await resolveBase()
      const res = await doFetch(`${base}/ops/ai/${encodeURIComponent(threadId)}`, { credentials: 'include' })
      if (!res.ok) return AI_SEAM_STATE
      const body: unknown = await res.json().catch(() => null)
      if (!isAiPaneReadShape(body)) return AI_SEAM_STATE
      if (body.wired !== true) {
        // The route answered and said why it cannot help. Carry that reason up
        // verbatim rather than replacing it with the seam's "not wired yet",
        // which would now be a lie — it IS wired, it just cannot answer.
        return typeof body.unavailable === 'string' && body.unavailable
          ? { ...AI_SEAM_STATE, unavailable: body.unavailable, suggestions: [] }
          : AI_SEAM_STATE
      }
      const priority = Number(body.priority)
      return {
        wired: true,
        suggestions: Array.isArray(body.suggestions) ? body.suggestions : [],
        priority: priority >= 1 && priority <= 5 ? ((priority | 0) as PriorityLevel) : undefined,
      }
    } catch {
      return AI_SEAM_STATE
    }
  }

  return { read }
}

/** The singleton every real caller (ContextPane) uses — same pattern as `ops` in ../../lib/ops.ts. */
export const aiOps = createAiOpsClient()
